import { useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { TOKEN_KEY } from "lib/axios"
import { useLogoutMutation } from "modules/auth/hooks/useLogoutMutation"
import { authEmployeeQuery } from "modules/auth/lib/authEmployeeQuery"
import { resetDeveloperMode } from "modules/shared/lib/developerMode"
import { createContext, useState } from "react"
import { flushSync } from "react-dom"
import { LoginResponseDataSchema } from "schema"
import { z } from "zod"

export type AuthenticationStatus = "authenticated" | "not_authenticated" | "unknown"

type AuthContextType = {
  authStatus: AuthenticationStatus
  setAuthStatus: (value: AuthenticationStatus) => void
  onLogin: (
    data: z.infer<typeof LoginResponseDataSchema>,
    router: ReturnType<typeof useRouter>,
  ) => void
  onLogout: (router: ReturnType<typeof useRouter>) => void
}

const authContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const [authStatus, setAuthStatus] = useState<AuthenticationStatus>("unknown")
  const queryClient = useQueryClient()
  const { mutateAsync: logoutMutation } = useLogoutMutation()

  async function onLogin(
    data: z.infer<typeof LoginResponseDataSchema>,
    router: ReturnType<typeof useRouter>,
  ) {
    resetDeveloperMode()
    localStorage.setItem(TOKEN_KEY, data.token)

    await queryClient.fetchQuery({ ...authEmployeeQuery(true), staleTime: 0 })

    flushSync(() => setAuthStatus("authenticated"))

    const redirect = new URLSearchParams(window.location.search).get("redirect")
    let resolvedPath = "/insights"
    if (redirect) {
      resolvedPath = decodeURIComponent(redirect)
    }

    router.navigate({ to: resolvedPath })
  }

  async function onLogout(router: ReturnType<typeof useRouter>) {
    resetDeveloperMode()
    setAuthStatus("not_authenticated")
    await logoutMutation()
    localStorage.removeItem(TOKEN_KEY)
    queryClient.clear()

    router.navigate({ to: "/login" })
  }

  const value = {
    authStatus,
    setAuthStatus,
    onLogin,
    onLogout,
  }

  return <authContext.Provider value={value}>{children}</authContext.Provider>
}

AuthProvider.authContext = authContext
